import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AttemptSummary } from '../types';
import { AttemptStatusBadge } from './AttemptStatusBadge';
import { AttemptProgress } from './AttemptProgress';
import { ResumeAttemptButton } from './ResumeAttemptButton';
import { useTranslation } from '../../../i18n';

interface AttemptCardProps {
  attempt: AttemptSummary;
}

export const AttemptCard: React.FC<AttemptCardProps> = ({ attempt }) => {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();

  const formattedDate = attempt.startedAt
    ? new Date(attempt.startedAt).toLocaleDateString(currentLanguage === 'hi' ? 'hi-IN' : undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : t('attempts.meta.not_started', undefined, 'Not started');

  const isCompleted = ['COMPLETED', 'SUBMITTED', 'EVALUATED', 'SCORED'].includes(attempt.status);
  const score = attempt.score !== null && attempt.score !== undefined ? Math.round(Number(attempt.score)) : null;
  const isPassed = typeof (attempt as any).isPassed === 'boolean' ? (attempt as any).isPassed : (score !== null ? score >= 70 : false);

  const handleCardClick = () => {
    if (isCompleted) {
      navigate(`/attempts/${attempt.id}/results`);
    } else {
      navigate(`/attempts/${attempt.id}`);
    }
  };

  const scenarioTitle = attempt.scenario?.title || (attempt as any).scenarioTitle || t('nav.simulations', undefined, 'Simulation Scenario');
  const rawCategory = attempt.scenario?.category || (attempt as any).category || 'Customer Service';
  const description = attempt.scenario?.description || (attempt as any).description || 'Dialogue simulation and behavioral rubric evaluation session.';
  const rawDifficulty = attempt.scenario?.difficulty || (attempt as any).difficulty || 'Intermediate';

  const translateCategory = (cat: string): string => {
    const catMap: Record<string, string> = {
      'customer service': 'category.customer_service',
      'sales': 'category.sales',
      'compliance': 'category.compliance',
      'de-escalation': 'category.de_escalation',
      'coaching': 'category.coaching',
      'general scenario': 'category.general_scenario',
    };
    const key = catMap[cat.trim().toLowerCase()];
    return key ? t(key, undefined, cat) : cat;
  };

  const translateDifficulty = (diff: string): string => {
    const diffMap: Record<string, string> = {
      'beginner': 'difficulty.beginner',
      'intermediate': 'difficulty.intermediate',
      'advanced': 'difficulty.advanced',
      'standard': 'difficulty.standard',
    };
    const key = diffMap[diff.trim().toLowerCase()];
    return key ? t(key, undefined, diff) : diff;
  };

  return (
    <div
      onClick={handleCardClick}
      className="attempt-card card"
      style={{
        background: 'var(--white)',
        borderRadius: '14px',
        border: '1px solid var(--line)',
        padding: '18px 20px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'all 0.2s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: '100%', minWidth: 0 }}>
        {/* Top Bar: Category + Status Badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '8px' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--blue)' }}>
            {translateCategory(rawCategory)}
          </span>
          <AttemptStatusBadge status={attempt.status} />
        </div>

        {/* Title */}
        <h3 style={{ fontFamily: 'var(--display)', fontSize: '16.5px', fontWeight: 600, color: 'var(--ink)', margin: '0 0 6px', lineHeight: 1.35, wordBreak: 'break-word' }}>
          {scenarioTitle}
        </h3>

        {/* Learner & Organization Context Badges (for Admins) */}
        {(attempt.learnerName || attempt.learnerEmail || attempt.tenantName) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 }}>
            {(attempt.learnerName || attempt.learnerEmail) && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#1e40af',
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  padding: '2px 7px',
                  borderRadius: '6px',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={attempt.learnerEmail || undefined}
              >
                👤 {attempt.learnerName || attempt.learnerEmail}
              </span>
            )}
            {attempt.tenantName && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#4338ca',
                  background: '#eef2ff',
                  border: '1px solid #c7d2fe',
                  padding: '2px 7px',
                  borderRadius: '6px',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                🏢 {attempt.tenantName}
              </span>
            )}
          </div>
        )}

        {/* Description */}
        <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '0 0 14px', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {description}
        </p>

        {/* Attempt Metadata Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', padding: '12px 14px', background: 'var(--surface)', borderRadius: '10px', marginBottom: '14px', fontSize: '12px', maxWidth: '100%', minWidth: 0 }}>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <span style={{ color: 'var(--faint)', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {t('attempts.meta.attempt', undefined, 'Attempt')}
            </span>
            <span style={{ fontWeight: 700, color: 'var(--ink)' }}>#{attempt.attemptNumber}</span>
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <span style={{ color: 'var(--faint)', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {t('attempts.meta.difficulty', undefined, 'Difficulty')}
            </span>
            <span style={{ fontWeight: 600, color: 'var(--ink)', textTransform: 'capitalize' }}>
              {translateDifficulty(rawDifficulty)}
            </span>
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <span style={{ color: 'var(--faint)', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {t('attempts.meta.date', undefined, 'Date')}
            </span>
            <span style={{ color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
              {formattedDate}
            </span>
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <span style={{ color: 'var(--faint)', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {t('attempts.meta.evaluation', undefined, 'Evaluation')}
            </span>
            {isCompleted && score !== null ? (
              <span style={{ fontWeight: 700, color: isPassed ? '#166534' : '#B45309', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                {isPassed ? `✓ ${score}%` : `✕ ${score}%`}
              </span>
            ) : (
              <span style={{ color: 'var(--muted)' }}>
                {t('status.in_progress', undefined, 'In Progress')}
              </span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: '14px' }}>
          <AttemptProgress
            currentTurn={attempt.currentTurn}
            maxTurns={attempt.maxTurns}
            durationSeconds={attempt.durationSeconds}
            timeLimitSeconds={attempt.timeLimitSeconds}
          />
        </div>
      </div>

      {/* Action Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid var(--line)', gap: '8px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isCompleted) {
              navigate(`/attempts/${attempt.id}/results`);
            } else {
              navigate(`/attempts/${attempt.id}`);
            }
          }}
          style={{ border: 0, background: 'none', color: 'var(--blue)', fontSize: '13px', fontWeight: 700, cursor: 'pointer', padding: 0 }}
        >
          {isCompleted ? t('attempts.view_scorecard', undefined, 'View Scorecard →') : t('attempts.view_attempt', undefined, 'View Attempt →')}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {attempt.canResume && (
            <ResumeAttemptButton
              attemptId={attempt.id}
              scenarioId={attempt.simulationId}
              canResume={attempt.canResume}
              size="sm"
            />
          )}

          {attempt.scenario && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/attempts/history?simulationId=${attempt.simulationId}`);
              }}
              style={{ border: 0, background: 'none', color: 'var(--muted)', fontSize: '12px', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
            >
              {t('attempts.all_attempts_link', undefined, 'All Attempts')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
